import * as Sentry from "@sentry/react";
import { lazy, useEffect } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider, QueryCache, MutationCache } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import posthog from "posthog-js";
import { PostHogProvider } from "posthog-js/react";
import { capturePageview } from "@/lib/analytics";

const SentryRoutes = Sentry.withSentryReactRouterV6Routing(Routes);

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  return null;
}

function PostHogPageviewTracker() {
  const { pathname } = useLocation();
  useEffect(() => { capturePageview(); }, [pathname]);
  return null;
}
import { AuthProvider } from "@/contexts/AuthContext";
import { FABProvider } from "@/contexts/FABContext";
import ProtectedRoute from "@/components/ProtectedRoute";
import ProGateRoute from "@/components/ProGateRoute";
import AppLayout from "@/components/AppLayout";
import RouteSuspense from "@/components/RouteSuspense";
import Landing from "./pages/Landing";
import Login from "./pages/Login";

// Retry dynamic imports once on failure (handles stale chunks after rebuild)
function lazyRetry(factory: () => Promise<any>) {
  return lazy(() =>
    factory().catch(() => {
      // Force reload to get fresh chunk manifest
      window.location.reload();
      return new Promise(() => {}); // never resolves — page is reloading
    })
  );
}

const Dashboard = lazyRetry(() => import("./pages/Index"));
const AssetList = lazyRetry(() => import("./pages/AssetList"));
const AssetForm = lazyRetry(() => import("./pages/AssetForm"));
const AssetDetail = lazyRetry(() => import("./pages/AssetDetail"));

// Lazy-loaded non-critical routes
const Insights = lazyRetry(() => import("./pages/Insights"));
const LocationDetail = lazyRetry(() => import("./pages/LocationDetail"));
const DraftReview = lazyRetry(() => import("./pages/DraftReview"));
const BulkCapture = lazyRetry(() => import("./pages/BulkCapture"));
const ReceiptForwarding = lazyRetry(() => import("./pages/ReceiptForwarding"));
const Settings = lazyRetry(() => import("./pages/settings/Settings"));
const TermsOfUse = lazyRetry(() => import("./pages/TermsOfUse"));
const PrivacyPolicy = lazyRetry(() => import("./pages/PrivacyPolicy"));
const Blog = lazyRetry(() => import("./pages/Blog"));
const BlogPost = lazyRetry(() => import("./pages/BlogPost"));
const Upgrade = lazyRetry(() => import("./pages/Upgrade"));

const InsuranceReport = lazyRetry(() => import("./pages/InsuranceReport"));
const InsuranceReportPrint = lazyRetry(() => import("./pages/InsuranceReportPrint"));
const AcvRcvCalculator = lazyRetry(() => import("./pages/tools/AcvRcvCalculator"));
const NotFound = lazyRetry(() => import("./pages/NotFound"));


const queryClient = new QueryClient({
  queryCache: new QueryCache({
    onError: (error, query) => {
      Sentry.captureException(error, {
        tags: { queryKey: JSON.stringify(query.queryKey) },
      });
    },
  }),
  mutationCache: new MutationCache({
    onError: (error) => {
      Sentry.captureException(error);
    },
  }),
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      gcTime: 5 * 60 * 1000,
    },
  },
});

const App = () => (
  <HelmetProvider>
  <PostHogProvider client={posthog}>
  <QueryClientProvider client={queryClient}>
    <AuthProvider>
      <FABProvider>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <BrowserRouter>
          <ScrollToTop />
          <PostHogPageviewTracker />
          <SentryRoutes>
            <Route path="/" element={<Landing />} />
            <Route path="/login" element={<Login />} />
            <Route
              path="/dashboard"
              element={
                <ProtectedRoute>
                  <AppLayout>
                    <RouteSuspense skeleton="dashboard"><Dashboard /></RouteSuspense>
                  </AppLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/assets"
              element={
                <ProtectedRoute>
                  <AppLayout>
                    <RouteSuspense skeleton="asset-list"><AssetList /></RouteSuspense>
                  </AppLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/assets/new"
              element={
                <ProtectedRoute>
                  <AppLayout>
                    <RouteSuspense skeleton="generic"><AssetForm /></RouteSuspense>
                  </AppLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/assets/:id"
              element={
                <ProtectedRoute>
                  <AppLayout>
                    <RouteSuspense skeleton="asset-detail"><AssetDetail /></RouteSuspense>
                  </AppLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/assets/:id/edit"
              element={
                <ProtectedRoute>
                  <AppLayout>
                    <RouteSuspense skeleton="generic"><AssetForm /></RouteSuspense>
                  </AppLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/insights"
              element={
                <ProtectedRoute>
                  <AppLayout>
                    <RouteSuspense skeleton="insights"><Insights /></RouteSuspense>
                  </AppLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/locations/:id"
              element={
                <ProtectedRoute>
                  <AppLayout>
                    <RouteSuspense skeleton="location-detail"><LocationDetail /></RouteSuspense>
                  </AppLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/assets/bulk"
              element={
                <ProtectedRoute>
                  <AppLayout>
                    <RouteSuspense skeleton="generic">
                      <ProGateRoute featureKey="bulk_capture"><BulkCapture /></ProGateRoute>
                    </RouteSuspense>
                  </AppLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/assets/review"
              element={
                <ProtectedRoute>
                  <AppLayout>
                    <RouteSuspense skeleton="generic"><DraftReview /></RouteSuspense>
                  </AppLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/settings"
              element={
                <ProtectedRoute>
                  <AppLayout>
                    <RouteSuspense skeleton="settings"><Settings /></RouteSuspense>
                  </AppLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/settings/forwarding"
              element={
                <ProtectedRoute>
                  <AppLayout>
                    <RouteSuspense skeleton="settings"><ReceiptForwarding /></RouteSuspense>
                  </AppLayout>
                </ProtectedRoute>
              }
            />
            <Route path="/terms" element={<RouteSuspense skeleton="generic"><TermsOfUse /></RouteSuspense>} />
            <Route path="/privacy" element={<RouteSuspense skeleton="generic"><PrivacyPolicy /></RouteSuspense>} />
            <Route path="/blog" element={<RouteSuspense skeleton="generic"><Blog /></RouteSuspense>} />
            <Route path="/blog/:slug" element={<RouteSuspense skeleton="generic"><BlogPost /></RouteSuspense>} />
            <Route path="/tools/acv-rcv-calculator" element={<RouteSuspense skeleton="generic"><AcvRcvCalculator /></RouteSuspense>} />
            <Route path="/settings/terms" element={<RouteSuspense skeleton="generic"><TermsOfUse /></RouteSuspense>} />
            <Route
              path="/upgrade"
              element={
                <ProtectedRoute>
                  <AppLayout>
                    <RouteSuspense skeleton="generic"><Upgrade /></RouteSuspense>
                  </AppLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/report/print"
              element={
                <ProtectedRoute>
                  <RouteSuspense skeleton="generic"><InsuranceReportPrint /></RouteSuspense>
                </ProtectedRoute>
              }
            />
            <Route
              path="/report/:reportId"
              element={
                <ProtectedRoute>
                  <RouteSuspense skeleton="generic"><InsuranceReport /></RouteSuspense>
                </ProtectedRoute>
              }
            />
            <Route path="*" element={<RouteSuspense skeleton="generic"><NotFound /></RouteSuspense>} />
          </SentryRoutes>
        </BrowserRouter>
      </TooltipProvider>
      </FABProvider>
    </AuthProvider>
  </QueryClientProvider>
  </PostHogProvider>
  </HelmetProvider>
);

export default App;
